// import components
import { Box, TextField } from '@mui/material';
import { VirtualizedAutocomplete } from 'components/shared';

// import constants
import { PRIMARY } from 'constants/colors';

// import hooks
import { HTMLAttributes, ReactNode, SyntheticEvent, useState } from 'react';

// import queries
import { queries } from 'RestAPIs/rapini';

// import types
import { DutyCodeSelectorProps, Option } from './DutyCodeSelector.types';
import { DutyCode } from 'generated';

// import utils
import { matchSorter } from 'match-sorter';
import { formatTime } from 'utils/date';
import {
  StyledLabelTruncateTypography,
  StyledListItem,
  StyledListItemBox,
  StyledListItemTimeBox
} from './DutyCodeSelector.styles';

const DutyCodeSelector = (props: DutyCodeSelectorProps) => {
  const {
    disabled,
    field,
    fieldState,
    fullWidth,
    size = 'medium',
    variant = 'standard',
    label,
    onChange,
    referenceDate,
    required,
    value
  } = props;

  const { data: dutyCodes } = queries.useGetDutyCodes(
    referenceDate?.toISOString()
  );

  const [timeHighlight, setTimeHighlight] = useState<boolean>(false);

  const options =
    dutyCodes
      ?.map((dc) => ({
        dutyCodeId: dc.dutyCodeId,
        value: dc.dutyCodeId,
        validFrom: dc.validFrom,
        validTo: dc.validTo,
        label: `${dc.code || dc.dutyCode}`,
        periods: dc.periods?.map((period) => ({
          ...period,
          startTimeString: formatTime(period.startTime),
          endTimeString: formatTime(period.endTime)
        })),
        description: dc.description
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const renderOption = (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: Option
  ) => {
    const styleLi = (
      <StyledListItem
        {...props}
        data-option-size={Math.max(
          ((option.periods?.length || 0) + 1) * 24,
          42
        )}
      >
        <StyledLabelTruncateTypography>
          {option.label}
        </StyledLabelTruncateTypography>
        <StyledListItemBox>
          {option.periods?.map((period) => (
            <StyledListItemTimeBox key={period.dutyCodePeriodId}>
              <Box>
                {formatTime(period.startTime)} - {formatTime(period.endTime)}
              </Box>
              <Box>{period.dutyHourType?.description}</Box>
            </StyledListItemTimeBox>
          ))}
        </StyledListItemBox>
      </StyledListItem>
    );
    return styleLi;
  };

  const filterOptions = (options: Option[], { inputValue }) => {
    const getTimeFilterRegex = (a = '', b = '') =>
      new RegExp(!a || !b ? `^${a || b}$` : `^(${a})-(${b})$`, 'gi');

    const startAndEndTimeRegex = /^(\d{2}:\d{2})\s?-\s?(\d{2}:\d{2})$/i;

    const filteredOptions = options.filter((option) => {
      const { periods } = option;

      return (periods || []).some((period) => {
        if (startAndEndTimeRegex.test(inputValue || '')) {
          const [, start, end] = startAndEndTimeRegex.exec(
            inputValue || ''
          ) || ['', '', ''];
          const time = getTimeFilterRegex(start, end);
          // A specific start and end time is provided
          return time.test(`${period.startTimeString}-${period.endTimeString}`);
        } else {
          // A single time that matches either the start or end time
          const time = getTimeFilterRegex(inputValue);
          return (
            time.test(period.startTimeString) || time.test(period.endTimeString)
          );
        }
      });
    });

    setTimeHighlight(filteredOptions.length > 0);
    return filteredOptions.length > 0
      ? filteredOptions
      : matchSorter(options, inputValue, {
          keys: ['label', 'description', 'periods.*.description']
        });
  };

  const getItemSize = (option: ReactNode | React.ReactPortal) => {
    return Math.max(42, (option as any).props['data-option-size'] || 0);
  };

  const onOptionChange = (
    event: SyntheticEvent<Element, Event>,
    value: NonNullable<string | Option> | (string | Option)[] | null
  ) => {
    if (typeof value === 'string') {
      onChange &&
        onChange(event, dutyCodes?.find((dht) => dht.dutyCodeId === +value));
    } else if (Array.isArray(value)) {
      const ids = value.map((v) => (typeof v === 'string' ? +v : v.dutyCodeId));
      onChange &&
        onChange(
          event,
          dutyCodes?.filter((dht) => ids.indexOf(dht.dutyCodeId) > -1)
        );
    } else {
      value &&
        onChange &&
        onChange(
          event,
          dutyCodes?.find((dht) => dht.dutyCodeId === value.dutyCodeId)
        );
    }
  };

  return (
    <VirtualizedAutocomplete
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          size={size}
          label={label}
          required={required}
          fullWidth={fullWidth}
          disabled={disabled}
          {...(timeHighlight && {
            sx: {
              input: {
                color: PRIMARY
              }
            }
          })}
          onChange={(
            event: SyntheticEvent<Element, Event>,
            dc?: DutyCode | DutyCode[]
          ) => {
            field?.onChange(event, dc);
            onChange && onChange(event, dc);
          }}
          error={!!fieldState?.error?.type}
          helperText={fieldState?.error?.message}
        />
      )}
      options={options}
      renderOption={renderOption}
      value={options?.find(
        ({ dutyCodeId }) => dutyCodeId === (field?.value || value)
      )}
      onChange={onOptionChange}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option?: Option, value?: Option) => {
        return option?.dutyCodeId === value?.dutyCodeId;
      }}
      getItemSize={getItemSize}
      disabled={disabled}
      isCustomHeight={true}
    />
  );
};

export default DutyCodeSelector;
